import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useAuth } from '../context/AuthContext';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import toast from 'react-hot-toast';
import { hadithBooks, hadithChapters } from '../data/hadithData';
import Footer from '../components/layout/Footer';

const {
    FiBookOpen, FiHeart, FiShare2,
    FiCopy, FiEdit3, FiList, FiCheck, FiFolder, FiX, FiLoader
} = FiIcons;

// API Mapping
const bookIdMap = {
    bukhari: 'bukhari',
    muslim: 'muslim',
    abudawud: 'abudawud',
    tirmidhi: 'tirmidhi',
    nasai: 'nasai',
    majah: 'ibnmajah'
};

const languageMap = {
    english: 'eng',
    urdu: 'urd',
    arabic: 'ara',
    bangla: 'ben',
    hindi: 'hin'
};

const Hadith = () => {
    const { user, addFavorite, addNote, updateHadithProgress, addCategory, addItemToCategory } = useAuth();

    // State
    const [selectedBook, setSelectedBook] = useState(null);
    const [selectedChapter, setSelectedChapter] = useState(null);
    const [selectedCategory, setSelectedCategory] = useState(null);
    const [currentHadiths, setCurrentHadiths] = useState([]);
    const [isLoading, setIsLoading] = useState(false);

    // Settings from User Preferences with defaults
    const [fontSize, setFontSize] = useState(user?.preferences?.fontSize || 'medium');
    const [translation, setTranslation] = useState(user?.preferences?.translation || 'english');

    // Interactive States
    const [activeNoteHadith, setActiveNoteHadith] = useState(null);
    const [noteContent, setNoteContent] = useState('');
    const [showCategoryModal, setShowCategoryModal] = useState(false);
    const [activeCategoryHadith, setActiveCategoryHadith] = useState(null);
    const [newCategoryName, setNewCategoryName] = useState('');

    const fontSizes = {
        small: 'text-base',
        medium: 'text-xl',
        large: 'text-2xl',
        xlarge: 'text-3xl'
    };

    // Sync preferences
    useEffect(() => {
        if (user?.preferences) {
            if (user.preferences.fontSize) setFontSize(user.preferences.fontSize);
            if (user.preferences.translation) setTranslation(user.preferences.translation);
        }
    }, [user]);

    // Fetch Hadiths when Chapter or Translation changes
    useEffect(() => {
        const fetchHadiths = async () => {
            if (!selectedBook || !selectedChapter) return;

            setIsLoading(true);
            setCurrentHadiths([]);

            const bookSlug = bookIdMap[selectedBook.id];
            const langSlug = languageMap[translation] || 'eng';
            const chapterNum = selectedChapter.number;

            try {
                // Always fetch Arabic for the original text
                const arabicUrl = `https://cdn.jsdelivr.net/gh/fawazahmed0/hadith-api@1/editions/ara-${bookSlug}/sections/${chapterNum}.json`;
                const arabicRes = await fetch(arabicUrl);
                const arabicData = await arabicRes.json();

                let translationData = { hadiths: [] };

                // Fetch Translation if not Arabic Only
                if (translation !== 'arabic') {
                    // Try to fetch specific translation
                    const transUrl = `https://cdn.jsdelivr.net/gh/fawazahmed0/hadith-api@1/editions/${langSlug}-${bookSlug}/sections/${chapterNum}.json`;
                    try {
                        const transRes = await fetch(transUrl);
                        if (transRes.ok) {
                            translationData = await transRes.json();
                        } else if (langSlug !== 'eng') {
                            // Fallback to English if selected language fails
                            const fallbackRes = await fetch(`https://cdn.jsdelivr.net/gh/fawazahmed0/hadith-api@1/editions/eng-${bookSlug}/sections/${chapterNum}.json`);
                            if (fallbackRes.ok) {
                                translationData = await fallbackRes.json();
                                toast.error(`Translation not available for ${translation}, showing English.`);
                            }
                        }
                    } catch (err) {
                        console.warn("Translation fetch failed", err);
                    }
                }

                // Merge Data
                const mergedHadiths = arabicData.hadiths.map((araHadith, index) => {
                    // Find matching translation by hadithnumber (most reliable)
                    const transHadith = translationData.hadiths.find(h => h.hadithnumber === araHadith.hadithnumber) ||
                        translationData.hadiths[index] || {}; // Fallback to index if number missing

                    return {
                        id: araHadith.hadithnumber,
                        hadithNumber: araHadith.hadithnumber,
                        volume: selectedChapter.id, // Using chapter ID as volume/section proxy
                        bookNumber: selectedBook.id,
                        arabic: araHadith.text,
                        english: transHadith.text || '', // Translation text
                        narrator: '', // API doesn't separate narrator consistently
                        reference: `Hadith ${araHadith.hadithnumber}`,
                        grade: (araHadith.grades && araHadith.grades.length > 0) ? araHadith.grades[0].grade : ''
                    };
                });

                setCurrentHadiths(mergedHadiths);

            } catch (error) {
                console.error("Failed to fetch hadiths:", error);
                toast.error("Failed to load hadiths. Please try again.");
            } finally {
                setIsLoading(false);
            }
        };

        fetchHadiths();
    }, [selectedBook, selectedChapter, translation]);

    const handleBookSelect = (book) => {
        setSelectedBook(book);
        setSelectedChapter(null);
        setSelectedCategory(null);
        setCurrentHadiths([]);
    };

    const handleChapterSelect = (chapter) => {
        setSelectedChapter(chapter);
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    // Actions
    const handleFavorite = (hadith) => {
        if (!user) return toast.error('Please sign in');
        const favId = `${hadith.bookNumber}-${hadith.volume}-${hadith.hadithNumber}`;
        addFavorite({
            id: favId,
            type: 'hadith',
            surah: selectedBook.name,
            verse: hadith.hadithNumber,
            text: hadith.arabic,
            translation: hadith.english,
            reference: hadith.reference,
            date: new Date().toISOString()
        });
        // updateHadithProgress('favorite', `${hadith.bookNumber}-${hadith.valume}-${hadith.hadithNumber}`);
        toast.success('Added to favorites');
    };

    const handleSaveNote = () => {
        if (!noteContent.trim()) return;
        addNote({
            type: 'hadith',
            reference: activeNoteHadith.reference,
            content: noteContent,
        });
        toast.success('Note saved');
        setActiveNoteHadith(null);
        setNoteContent('');
        updateHadithProgress('studied', activeNoteHadith.id);
    };

    const handleCopy = (hadith) => {
        const text = `${hadith.arabic}\n\n${hadith.english}\n\n${hadith.reference}`;
        navigator.clipboard.writeText(text);
        toast.success('Copied to clipboard');
    };

    const handleShare = (hadith) => {
        const text = `Read this Hadith: ${hadith.reference}\n${hadith.english}`;
        const url = window.location.href;
        const shareUrl = `https://twitter.com/intent/tweet?text=${encodeURIComponent(text)}&url=${encodeURIComponent(url)}`;
        window.open(shareUrl, '_blank');
    };

    const handleAddToCategory = async () => {

        if (!activeCategoryHadith || !newCategoryName.trim()) return;

        try {
            // 1️⃣ Create category and await response
            const response = await addCategory(newCategoryName);

            // 2️⃣ Extract the category ID from response
            const newCategoryId = response.data.id; // assuming API returns { id: ..., name: ... }

            // 3️⃣ Call your existing function to save Hadith to this new category
            handleSaveToExistingCategory(newCategoryId);

            // 4️⃣ UI updates
            toast.success(
                `Category "${newCategoryName}" created. Hadith saved to it!`
            );

            setNewCategoryName('');
            setShowCategoryModal(false);

        } catch (error) {
            console.error("Category creation failed:", error);
            toast.error(
                error.response?.data?.message || "Failed to create category"
            );
        }
    };

    const handleSaveToExistingCategory = (categoryId) => {
        if (!activeCategoryHadith) return;

        addItemToCategory(categoryId, {
            id: `hadith-${selectedBook.id}-${activeCategoryHadith.id}`,
            type: 'hadith',
            ref: activeCategoryHadith.reference,
            content: activeCategoryHadith.english
        });

        toast.success('Hadith saved to category');
        setShowCategoryModal(false);
    };

    // Derived Data
    const currentChapters = selectedBook ? (hadithChapters[selectedBook.id] || []) : [];
    const categoryItems = selectedCategory ? selectedCategory.items.filter(i => i.type === 'hadith') : [];

    return (
        <div className="min-h-screen pt-4 bg-gray-50 dark:bg-gray-900">
            <div className="max-w-7xl mx-auto space-y-4">

                {/* Header & Controls - Compact Layout */}
                <div className="flex flex-col lg:flex-row justify-between items-center gap-3 bg-white dark:bg-gray-800 p-3 rounded-xl border border-gray-200 dark:border-gray-700 shadow-sm">
                    <div className="flex items-center gap-4 w-full lg:w-auto">
                        <h1 className="text-xl font-bold text-gray-900 dark:text-white shrink-0">Hadith Collection</h1>

                        {/* User Categories Dropdown - Right of Title */}
                        {user && (
                            <div className="relative group">
                                <button className="flex items-center gap-2 px-3 py-1.5 bg-emerald-50 dark:bg-emerald-900/20 text-emerald-700 dark:text-emerald-400 rounded-lg hover:bg-emerald-100 dark:hover:bg-emerald-900/40 transition-colors">
                                    <SafeIcon icon={FiFolder} className="w-4 h-4" />
                                    <span className="text-sm font-medium whitespace-nowrap">My Categories</span>
                                </button>
                                <div className="absolute top-full left-0 mt-2 w-56 bg-white dark:bg-gray-800 rounded-lg shadow-lg border border-gray-100 dark:border-gray-700 py-2 hidden group-hover:block z-50">
                                    {((user.categories || []).filter(cat => cat.items.some(item => item.item_type === "hadith"))).length === 0 ? (
                                        <div className="px-4 py-2 text-sm text-gray-500">No categories yet</div>
                                    ) : (
                                        (user.categories || [])
                                            .filter(cat => cat.items.some(item => item.item_type === "hadith"))
                                            .map(cat => (
                                                <button
                                                    key={cat.id}
                                                    onClick={() => setSelectedCategory(cat)}
                                                    className="w-full text-left px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700"
                                                >
                                                    {cat.name} ({cat.items.length})
                                                </button>
                                            ))
                                    )}
                                </div>
                            </div>
                        )}
                    </div>

                    <div className="flex flex-wrap items-center gap-2 w-full lg:w-auto justify-end">
                        {/* Translation Dropdown */}
                        <select
                            value={translation}
                            onChange={(e) => setTranslation(e.target.value)}
                            className="px-2 py-1.5 bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-lg text-sm focus:ring-2 focus:ring-emerald-500"
                        >
                            <option value="english">English</option>
                            <option value="urdu">Urdu</option>
                            <option value="arabic">Arabic Only</option>
                            <option value="bangla">Bangla</option>
                            <option value="hindi">Hindi</option>
                        </select>

                        {/* Font Size Dropdown */}
                        <select
                            value={fontSize}
                            onChange={(e) => setFontSize(e.target.value)}
                            className="px-2 py-1.5 bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-lg text-sm focus:ring-2 focus:ring-emerald-500"
                        >
                            <option value="small">Small Text</option>
                            <option value="medium">Medium Text</option>
                            <option value="large">Large Text</option>
                            <option value="xlarge">Extra Large</option>
                        </select>
                    </div>
                </div>

                {/* Compact Books Bar */}
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-2">
                    {hadithBooks.map((book) => (
                        <motion.button
                            key={book.id}
                            whileHover={{ scale: 1.02 }}
                            whileTap={{ scale: 0.98 }}
                            onClick={() => handleBookSelect(book)}
                            className={`p-2 rounded-lg text-center transition-all border flex flex-col items-center justify-center h-full ${selectedBook?.id === book.id
                                    ? 'bg-emerald-600 text-white border-emerald-600 shadow-sm ring-1 ring-emerald-200 dark:ring-emerald-900'
                                    : 'bg-white dark:bg-gray-800 text-gray-900 dark:text-white border-gray-200 dark:border-gray-700 hover:border-emerald-300 hover:bg-gray-50 dark:hover:bg-gray-700'
                                }`}
                        >
                            <span className="font-bold text-sm truncate w-full">{book.name}</span>
                            <div className="flex items-center gap-1.5 mt-1">
                                <span className={`text-xs ${selectedBook?.id === book.id ? 'text-emerald-100' : 'text-gray-500 dark:text-gray-400'}`}>
                                    {book.totalHadiths}
                                </span>
                                <span className={`font-arabic text-xs ${selectedBook?.id === book.id ? 'text-emerald-100' : 'text-gray-400'}`}>
                                    {book.arabicName}
                                </span>
                            </div>
                        </motion.button>
                    ))}
                </div>

                <div className="grid lg:grid-cols-12 gap-4 min-h-[500px]">
                    {/* Left Sidebar: Chapters */}
                    <div className="lg:col-span-3 bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 overflow-hidden flex flex-col h-[400px] lg:h-[calc(100vh-180px)] lg:sticky lg:top-4">
                        <div className="p-3 border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-750">
                            <h3 className="font-semibold text-sm text-gray-900 dark:text-white uppercase tracking-wider">
                                {selectedBook ? 'Chapters' : 'Select a Book'}
                            </h3>
                        </div>

                        <div className="overflow-y-auto flex-1 p-2 space-y-1 custom-scrollbar">
                            {!selectedBook ? (
                                <div className="text-center py-10 text-gray-500">
                                    <SafeIcon icon={FiBookOpen} className="w-8 h-8 mx-auto mb-2 opacity-50" />
                                    <p className="text-sm">Select a book above</p>
                                </div>
                            ) : (
                                currentChapters.map((chapter) => (
                                    <button
                                        key={chapter.id}
                                        onClick={() => handleChapterSelect(chapter)}
                                        className={`w-full text-left p-2 rounded-lg text-sm transition-colors ${selectedChapter?.id === chapter.id
                                                ? 'bg-emerald-50 dark:bg-emerald-900/20 text-emerald-700 dark:text-emerald-400 font-medium'
                                                : 'text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-700'
                                            }`}
                                    >
                                        <div className="flex justify-between items-start gap-2">
                                            <span className="line-clamp-2">{chapter.number}. {chapter.title}</span>
                                            <span className="text-xs bg-gray-100 dark:bg-gray-700 px-1.5 py-0.5 rounded text-gray-500 shrink-0">
                                                {chapter.hadithCount}
                                            </span>
                                        </div>
                                    </button>
                                ))
                            )}
                        </div>
                    </div>

                    {/* Main Content: Hadiths */}
                    <div className="lg:col-span-9 bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-6 min-h-[600px]">
                        {selectedCategory ? (
                            // Category View
                            <div>
                                <div className="flex justify-between items-center mb-6 border-b border-gray-200 dark:border-gray-700 pb-4">
                                    <h2 className="text-xl font-bold text-gray-900 dark:text-white">
                                        Category: {selectedCategory.name}
                                    </h2>
                                    <button onClick={() => setSelectedCategory(null)} className="text-sm text-emerald-600 hover:underline">
                                        Clear Filter
                                    </button>
                                </div>
                                {categoryItems.length === 0 ? (
                                    <p className="text-gray-500">No hadiths in this category.</p>
                                ) : (
                                    <div className="space-y-6">
                                        {categoryItems.map((item, idx) => (
                                            <div key={idx} className="p-4 border border-gray-200 rounded-lg">
                                                <p className="font-semibold text-sm text-gray-500 mb-2">{item.ref}</p>
                                                <p className="text-gray-800 dark:text-gray-200">{item.content}</p>
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>
                        ) : !selectedChapter ? (
                            <div className="h-full flex flex-col items-center justify-center text-gray-500 text-center p-8">
                                <SafeIcon icon={FiList} className="w-16 h-16 mb-4 text-emerald-200 dark:text-emerald-900" />
                                <h3 className="text-xl font-medium text-gray-900 dark:text-white mb-2">Select a Chapter</h3>
                                <p>Choose a chapter from the sidebar to view its Hadiths.</p>
                            </div>
                        ) : (
                            // Hadith List View
                            <div className="space-y-8">
                                <div className="border-b border-gray-200 dark:border-gray-700 pb-4 mb-6">
                                    <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-2">
                                        {selectedChapter.title}
                                    </h2>
                                    <p className="text-gray-500 dark:text-gray-400 font-arabic text-lg" dir="rtl">
                                        {selectedChapter.arabicTitle}
                                    </p>
                                </div>

                                {isLoading ? (
                                    <div className="flex flex-col items-center justify-center py-20">
                                        <motion.div
                                            animate={{ rotate: 360 }}
                                            transition={{ repeat: Infinity, duration: 1, ease: "linear" }}
                                            className="text-emerald-600 mb-4"
                                        >
                                            <SafeIcon icon={FiLoader} className="w-8 h-8" />
                                        </motion.div>
                                        <p className="text-gray-500">Loading Hadiths...</p>
                                    </div>
                                ) : currentHadiths.length === 0 ? (
                                    <p className="text-center text-gray-500 py-10">
                                        No hadiths available for this chapter in the selected translation.
                                    </p>
                                ) : (
                                    currentHadiths.map((hadith) => (
                                        <motion.div
                                            key={hadith.id}
                                            initial={{ opacity: 0, y: 20 }}
                                            animate={{ opacity: 1, y: 0 }}
                                            className="group relative border-b border-gray-100 dark:border-gray-700 last:border-0 pb-8"
                                        >
                                            {/* Meta Header */}
                                            <div className="flex flex-wrap items-center gap-2 mb-4 text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                                                <span className="bg-gray-100 dark:bg-gray-700 px-2 py-1 rounded">
                                                    {hadith.reference}
                                                </span>
                                                <span>• Vol {hadith.volume}</span>
                                                <span>• Book {hadith.bookNumber}</span>
                                                <span>• Hadith {hadith.hadithNumber}</span>
                                                {hadith.grade && (
                                                    <span className="ml-auto px-2 py-1 bg-green-50 text-green-700 rounded-full normal-case">
                                                        {hadith.grade}
                                                    </span>
                                                )}
                                            </div>

                                            {/* Content */}
                                            <div className="space-y-6">
                                                {hadith.arabic && (
                                                    <p className={`text-right font-arabic leading-loose text-gray-900 dark:text-white ${fontSizes.large}`} dir="rtl">
                                                        {hadith.arabic}
                                                    </p>
                                                )}
                                                {hadith.english && (
                                                    <p className={`text-gray-700 dark:text-gray-300 leading-relaxed ${fontSizes[fontSize]}`}>
                                                        {hadith.english}
                                                    </p>
                                                )}
                                                {hadith.narrator && (
                                                    <p className="text-sm text-gray-500 italic">
                                                        Narrated by: {hadith.narrator}
                                                    </p>
                                                )}
                                            </div>

                                            {/* Action Bar (Hover) */}
                                            <div className="flex items-center gap-2 mt-6 opacity-0 group-hover:opacity-100 transition-opacity duration-200">
                                                <button onClick={() => { setActiveNoteHadith(hadith); }} className="p-2 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-full transition-colors" title="Add Note">
                                                    <SafeIcon icon={FiEdit3} className="w-5 h-5" />
                                                </button>
                                                <button onClick={() => handleFavorite(hadith)} className="p-2 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-full transition-colors" title="Favorite">
                                                    <SafeIcon icon={FiHeart} className="w-5 h-5" />
                                                </button>
                                                <button onClick={() => handleCopy(hadith)} className="p-2 text-gray-400 hover:text-indigo-600 hover:bg-indigo-50 rounded-full transition-colors" title="Copy">
                                                    <SafeIcon icon={FiCopy} className="w-5 h-5" />
                                                </button>
                                                <button
                                                    onClick={() => { setActiveCategoryHadith(hadith); setShowCategoryModal(true); }}
                                                    className="p-2 text-gray-400 hover:text-orange-600 hover:bg-orange-50 rounded-full transition-colors"
                                                    title="Save to Category"
                                                >
                                                    <SafeIcon icon={FiFolder} className="w-5 h-5" />
                                                </button>
                                                <button onClick={() => handleShare(hadith)} className="p-2 text-gray-400 hover:text-purple-600 hover:bg-purple-50 rounded-full transition-colors" title="Share">
                                                    <SafeIcon icon={FiShare2} className="w-5 h-5" />
                                                </button>
                                            </div>
                                        </motion.div>
                                    ))
                                )}
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {/* Note Modal */}
            <AnimatePresence>
                {activeNoteHadith && (
                    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
                        <motion.div
                            initial={{ opacity: 0, scale: 0.95 }}
                            animate={{ opacity: 1, scale: 1 }}
                            exit={{ opacity: 0, scale: 0.95 }}
                            className="bg-white dark:bg-gray-800 rounded-xl shadow-xl w-full max-w-md p-6"
                        >
                            <h3 className="text-lg font-bold mb-4 text-gray-900 dark:text-white">Add Note</h3>
                            <p className="text-sm text-gray-500 mb-4">{activeNoteHadith.reference}</p>
                            <textarea
                                value={noteContent}
                                onChange={(e) => setNoteContent(e.target.value)}
                                className="w-full h-32 p-3 border rounded-lg mb-4 bg-gray-50 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                                placeholder="Write your reflections..."
                            />
                            <div className="flex justify-end gap-2">
                                <button onClick={() => setActiveNoteHadith(null)} className="px-4 py-2 text-gray-600 hover:bg-gray-100 rounded-lg">Cancel</button>
                                <button onClick={handleSaveNote} className="px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700">Save Note</button>
                            </div>
                        </motion.div>
                    </div>
                )}
            </AnimatePresence>

            {/* Category Modal */}
            <AnimatePresence>
                {showCategoryModal && (
                    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
                        <motion.div
                            initial={{ opacity: 0, scale: 0.95 }}
                            animate={{ opacity: 1, scale: 1 }}
                            exit={{ opacity: 0, scale: 0.95 }}
                            className="bg-white dark:bg-gray-800 rounded-xl shadow-xl w-full max-w-sm p-6"
                        >
                            <div className="flex justify-between items-center mb-4">
                                <h3 className="text-lg font-bold text-gray-900 dark:text-white">Save to Category</h3>
                                <button onClick={() => setShowCategoryModal(false)}><SafeIcon icon={FiX} className="text-gray-500" /></button>
                            </div>

                            {/* List existing categories */}
                            {user?.categories && user.categories.length > 0 && (
                                <div className="mb-4 space-y-2 max-h-40 overflow-y-auto">
                                    <p className="text-xs text-gray-500 font-semibold uppercase">Existing Categories</p>
                                    {user.categories
                                    .filter(cat => cat.items.some(item => item.item_type === "hadith"))
                                    .map(cat => (
                                        <button
                                        key={cat.id}
                                        onClick={() => handleSaveToExistingCategory(cat.id)}
                                        className="w-full text-left px-3 py-2 rounded hover:bg-gray-100 dark:hover:bg-gray-700 text-sm flex justify-between"
                                        >
                                        <span className="text-gray-800 dark:text-gray-200">+ {cat.name}</span>
                                        <span className="text-gray-400 text-xs">{cat.items.length} items</span>
                                        </button>
                                    ))}
                                </div>
                            )}

                            <div className="border-t border-gray-200 dark:border-gray-700 pt-4">
                                <p className="text-xs text-gray-500 font-semibold uppercase mb-2">Create New Category</p>
                                <div className="flex gap-2">
                                    <input
                                        type="text"
                                        value={newCategoryName}
                                        onChange={(e) => setNewCategoryName(e.target.value)}
                                        maxLength={50}
                                        placeholder="Name (max 15 chars)"
                                        className="flex-1 px-3 py-2 border rounded-lg text-sm bg-gray-50 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                                    />
                                    <button
                                        onClick={handleAddToCategory}
                                        disabled={!newCategoryName.trim()}
                                        className="px-3 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 disabled:opacity-50"
                                    >
                                        <SafeIcon icon={FiCheck} />
                                    </button>
                                </div>
                            </div>
                        </motion.div>
                    </div>
                )}
            </AnimatePresence>

            <Footer />
        </div>
    );
};

export default Hadith;